/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { CustomerCheckout } from "@lemmonhq/types";
import { calculateCartSubtotal } from "@lemmonhq/orders";
import { useMenu } from "@app/vue/providers/Menu";
import { getCharacterNameForCustomer } from "../../../pages/ShopGroupOrder/utils/packs";
import { useLocalization } from "@lemmonhq/uikit";

export interface CompletedOrdersSectionProps {}

export const CompletedOrdersSection =
  defineComponent<CompletedOrdersSectionProps>({
    name: "CompletedOrdersSection",
    props: [],
    setup(props) {
      const { realtime, groupOrder, shop } = useMenu();
      const { t } = useLocalization();
      const completedCheckouts = computed(() => {
        return groupOrder.value?.checkouts.filter(
          (el) => el.status === "completed",
        );
      });

      const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      };

      return () => {
        if (!completedCheckouts.value) {
          return null;
        }

        const groupMembers = realtime.groupState.value.members;

        return (
          <div class="space-y-1">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold text-base-content/70 flex items-center gap-2">
                <span class="material-symbols-outlined text-success">
                  check_circle
                </span>
                {t("customer.checkout_status.already_sent")}
              </h3>
              <div class="badge badge-success badge-sm">
                {t(
                  completedCheckouts.value.length === 1
                    ? "customer.checkout_status.order_count_one"
                    : "customer.checkout_status.order_count_many",
                  [completedCheckouts.value.length],
                )}
              </div>
            </div>

            <div class="space-y-2">
              {completedCheckouts.value.map((checkout) => {
                const member = groupMembers.find(
                  (m) => m.clientId === checkout.customer_id,
                );

                const characterName = groupOrder.value
                  ? getCharacterNameForCustomer(
                      groupOrder.value,
                      checkout.customer_id,
                    )
                  : null;

                const memberName =
                  characterName ||
                  member?.clientName ||
                  t("customer.checkout_status.unknown_member");

                return (
                  <div
                    key={checkout.id}
                    class="rounded bg-base-200 shadow-lg hover:shadow-xl border rounded-lg p-4"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-3">
                        <span class="material-symbols-outlined text-success">
                          restaurant
                        </span>
                        <div>
                          <div class="font-medium text-sm">{memberName}</div>
                          <div class="text-xs text-base-content/60 flex items-center gap-2">
                            <span>{formatTime(checkout.created_at)}</span>
                            <span>•</span>
                            <span class="uppercase">
                              {checkout.payment_method}
                            </span>
                            {checkout.order_id && (
                              <>
                                <span>•</span>
                                <span class="text-success">
                                  {t("customer.checkout_status.order_number", [
                                    checkout.order_id,
                                  ])}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div class="text-right">
                        <div class="font-semibold text-sm">
                          {t(
                            checkout.payload.cart.items.length === 1
                              ? "customer.checkout_status.item_count_one"
                              : "customer.checkout_status.item_count_many",
                            [checkout.payload.cart.items.length],
                          )}
                        </div>
                        <div class="text-xs text-base-content/60">
                          {shop.value.currency}{" "}
                          {calculateCartSubtotal(checkout.payload.cart).toFixed(
                            2,
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      };
    },
  });
